import {Typography} from '@mui/material';
import moment from 'moment';
import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import parseHTML from 'html-react-parser';
import {Box} from '@mui/system';
import {GetHandoverNotes} from '../../schema-types/GetHandoverNotes';
import {NoteFragment} from '../../schema-types/NoteFragment';
import {useHandoverNoteStyles} from './handoverNoteStyles';
import {getReviewReason} from './reviewReasonMap';
import {HandoverNoteFields} from '../../pages/HandoverNotesPage/HandoverNotesHelpers';
import {SaltusAny} from '../../utils/SaltusAny';
import theme, {themeColors} from '../../theme/Theme';
import {useAccountContext} from '../../contexts/AccountContext';

export const PreviousNotesV2: React.FC<{
  allPreviousNotes: GetHandoverNotes['previousNotes'];
  page: HandoverNoteFields;
}> = ({allPreviousNotes, page}) => {
  const {accountId} = useAccountContext();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const {reviewId} = useParams();

  const previousRelatedNotes = useMemo(() => {
    const relatedNotes: (NoteFragment & {
      reviewId: string;
      reviewType: string;
      reviewName: string;
    })[] = [];
    allPreviousNotes?.forEach((review): SaltusAny => {
      // anything that's not the current review is a previous review basically
      if (review.id === reviewId) {
        return;
      }

      review.notes?.forEach((note) => {
        if (note.displayName !== page) {
          return;
        }
        if (note.body && note.body !== 'None') {
          relatedNotes.push({
            ...note,
            body: note.body,
            markupBody: note.markupBody,
            reviewName: review.name || '',
            reviewId: review.id,
            reviewType: `${getReviewReason(review)?.text || review.reviewReason}`,
            dateCreated: review.dateBooked,
          });
        }
      });
    });
    return relatedNotes;
  }, [page, allPreviousNotes]);

  useEffect(() => {
    if (!previousRelatedNotes) {
      return;
    }
    for (const previousNote of previousRelatedNotes) {
      if (previousNote.displayName === page) {
        if (previousNote.body) {
          setOpen((x) => ({...x, [previousNote.id]: true}));
          return; // after the first non empty and relevant note, stop
        }
      }
    }
  }, [previousRelatedNotes]);

  const classes = useHandoverNoteStyles();
  if (!allPreviousNotes) {
    return <Typography sx={classes.noteTitle}>Error fetching previous notes</Typography>;
  }
  if (!previousRelatedNotes?.length) {
    return (
      <Typography sx={classes.noteTitle}>
        No historical {page.toLocaleLowerCase()} notes
      </Typography>
    );
  }

  return (
    <Box>
      <Typography pl={1} sx={classes.noteTitle}>
        {previousRelatedNotes.length} {reviewId ? 'previous' : ''}{' '}
        {page.toLocaleLowerCase()} note
        {previousRelatedNotes.length === 1 ? '' : 's'}:
      </Typography>
      <Box>
        {previousRelatedNotes.map((note, i) => {
          return (
            <Box
              p={1.5}
              pb={2}
              bgcolor={theme.palette.grey[100]}
              border={`1px solid ${themeColors.greyLight}`}
              mb={2}
              borderRadius={2}
              // borderBottom={`1px solid ${themeColors.greyLight}`}
              sx={{
                ':hover': {
                  background: theme.palette.grey[100],
                },
              }}
              key={note.id}
              display="flex"
              flexDirection="column"
            >
              {/* <Accordion

               onChange={() => {
                 const currentlyOpen = open[note.id];
                 setOpen((x) => ({...x, [note.id]: !currentlyOpen}));
               }}
               expanded={open[note.id] || false}
             >
               <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    onClick={(e) => {
                      const currentlyOpen = open[note.id];
                      setOpen((x) => ({...x, [note.id]: !currentlyOpen}));

                      e.stopPropagation();
                    }}
                  />
                }
              > */}

              <Box
                display="flex"
                flexDirection="row"
                alignContent="flex-start"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    component="a"
                    href={`/account/${accountId}/review-journey/${note.reviewId}/notes`}
                    target="_blank"
                    fontSize={16}
                    color={theme.palette.primary.main}
                  >
                    <strong>{note.reviewName}</strong>
                  </Typography>
                  <Typography fontSize={14} color={theme.palette.primary.main}>
                    {note.reviewType || 'Review'} (
                    {note.dateCreated
                      ? moment.utc(note.dateCreated).format('DD.MM.YYYY')
                      : 'No date'}
                    )
                  </Typography>
                </Box>
              </Box>

              <Box>
                <div style={{whiteSpace: 'pre-wrap'}}>
                  {note.markupBody ? parseHTML(note.markupBody) : note.body}
                </div>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
